import { createFileRoute, Link } from "@tanstack/react-router";
import { MessageCircle, Sparkles, ClipboardList, Stethoscope } from "lucide-react";
import { TrustBar } from "@/components/TrustBar";

export const Route = createFileRoute("/consult")({
  component: ConsultPage,
  head: () => ({
    meta: [
      { title: "Consult a TruWe expert. Personalised protocol guidance" },
      {
        name: "description",
        content:
          "Not sure which TruWe protocol is right for you? Take the 60-second quiz, chat with Vita our AI advisor, or book a 1:1 WhatsApp consult with a TruWe formulator.",
      },
      { property: "og:title", content: "Consult a TruWe expert" },
      {
        property: "og:description",
        content:
          "Personalised protocol guidance for magnesium, inositol, liver & gut support, built around your body, your goals and your absorption.",
      },
    ],
  }),
});

const OPTIONS = [
  {
    icon: ClipboardList,
    tag: "Fastest",
    title: "60-second protocol quiz",
    body:
      "Answer eight questions on sleep, cycle, energy and gut. We map you to the single TruWe formula (or bundle) most likely to move the needle first.",
    ctaLabel: "Take the quiz",
    to: "/quiz" as const,
  },
  {
    icon: Sparkles,
    tag: "24/7",
    title: "Ask Vita, our AI advisor",
    body:
      "Vita is trained on the entire TruWe formulary, every published study behind the actives, and post-purchase order lookup. Ask her anything, dosage, timing, stacking, side-effects.",
    ctaLabel: "Chat with Vita",
    href: "#vita" as const,
  },
  {
    icon: MessageCircle,
    tag: "1:1",
    title: "WhatsApp a TruWe formulator",
    body:
      "For complex cases PCOS + hypothyroid, IBS + magnesium sensitivity, pre-conception planning text our in-house R&D team on WhatsApp. Usually replies within a working day.",
    ctaLabel: "Open WhatsApp",
    external: "https://wa.me/919999999999?text=Hi%20TruWe%2C%20I%27d%20like%20a%20consult." as const,
  },
];

function ConsultPage() {
  return (
    <div>
      <TrustBar />
      <div className="mx-auto max-w-6xl px-4 py-14 md:px-8 md:py-20">
        <nav aria-label="Breadcrumb" className="mb-6 text-xs text-muted-foreground">
          <ol className="flex items-center gap-2">
            <li>
              <Link to="/" className="hover:text-brand">Home</Link>
            </li>
            <li aria-hidden>/</li>
            <li className="text-foreground">Consult</li>
          </ol>
        </nav>

        <header className="max-w-3xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">Personalised guidance</p>
          <h1 className="mt-2 font-display text-4xl font-bold text-brand-deep md:text-5xl">
            Talk to someone before you buy.
          </h1>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
            Supplements only work when they match what your body actually needs. Choose the level of hand-holding you want, an instant quiz, a conversation with our AI, or a real human formulator on WhatsApp.
          </p>
        </header>

        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {OPTIONS.map((o) => (
            <article
              key={o.title}
              className="flex flex-col justify-between rounded-3xl border border-border bg-card/50 p-6 transition hover:border-brand hover:shadow-md md:p-7"
            >
              <div>
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand/10 text-brand">
                    <o.icon className="h-5 w-5" />
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand">{o.tag}</span>
                </div>
                <h2 className="mt-4 font-display text-xl font-bold text-brand-deep">{o.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{o.body}</p>
              </div>
              <div className="mt-6">
                {o.to ? (
                  <Link
                    to={o.to}
                    className="inline-flex items-center rounded-full bg-brand-deep px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-white transition hover:-translate-y-0.5"
                  >
                    {o.ctaLabel}
                  </Link>
                ) : o.external ? (
                  <a
                    href={o.external}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center rounded-full bg-brand-deep px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-white transition hover:-translate-y-0.5"
                  >
                    {o.ctaLabel}
                  </a>
                ) : (
                  <a
                    href={o.href}
                    className="inline-flex items-center rounded-full bg-brand-deep px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-white transition hover:-translate-y-0.5"
                  >
                    {o.ctaLabel}
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>

        <section className="mt-16 rounded-3xl border border-border bg-brand-soft/40 p-8 md:p-12">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 text-brand">
                <Stethoscope className="h-5 w-5" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em]">TruWe promise</span>
              </div>
              <h2 className="mt-3 font-display text-2xl font-bold text-brand-deep md:text-3xl">
                We&apos;ll tell you when you don&apos;t need us.
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">
                If your symptoms point toward a medical workup thyroid panel, ferritin, vitamin D we&apos;ll say so before we sell you a sachet. Supplements are additive, not a substitute for diagnostics.
              </p>
            </div>
            <Link
              to="/shop"
              className="inline-flex items-center rounded-full border border-brand-deep bg-background px-6 py-3 text-xs font-bold uppercase tracking-[0.18em] text-brand-deep transition hover:bg-brand-deep hover:text-white"
            >
              Browse the formulary
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
